/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import NavCommon from "../../components/@navHeader";
import DialogCommon from "../../components/dialogCommon";
import NewTaskV2 from "../../components/dialogCommon/templates/NewTaskV2";
import CalendarHeader from "./components/CalendarHeader";
import TimeGrid from "./components/TimeGrid";
import EventDetailPopup from "./components/EventDetailPopup";
import NewEventModal from "./components/NewEventModal";
import MiniCalendar from "./components/MiniCalendar";
import { useCalendarNavigation } from "./hooks/useCalendarNavigation";
import {
  useCalendarEvents,
  useCreateEvent,
  useUpdateEvent,
  useDeleteEvent,
  useAcceptEvent,
  useDeclineEvent,
  useTentativelyAcceptEvent,
  useUserProfile,
} from "./hooks/useCalendarEvents";
import { CalendarEvent, NewEventPayload } from "./types";
import {
  useSalvarNoOutlookPreferencia,
  useSalvarNoOutlookMutation,
} from "../../services/api/AppWeb_Lead/salvar-no-outlook";
import * as S from "./styles";

export function Calendario() {
  const history = useHistory();

  const {
    currentDate,
    viewMode,
    setViewMode,
    navigate,
    goToDate,
    dateRange,
    getDayColumns,
  } = useCalendarNavigation();

  const {
    data: events = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useCalendarEvents(dateRange.start, dateRange.end);

  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();
  const acceptEvent = useAcceptEvent();
  const declineEvent = useDeclineEvent();
  const tentativeEvent = useTentativelyAcceptEvent();
  const { data: userProfile } = useUserProfile();

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [defaultDate, setDefaultDate] = useState<Date | undefined>();
  const [defaultHour, setDefaultHour] = useState<number | undefined>();
  const [miniCalendarOpen, setMiniCalendarOpen] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  const { data: salvarNoOutlookPref } = useSalvarNoOutlookPreferencia();
  const [syncEnabled, setSyncEnabled] = useState(false);
  const salvarNoOutlookMutation = useSalvarNoOutlookMutation();

  React.useEffect(() => {
    if (salvarNoOutlookPref !== undefined) {
      setSyncEnabled(salvarNoOutlookPref);
    }
  }, [salvarNoOutlookPref]);

  const handleSyncChange = (value: boolean) => {
    setSyncEnabled(value);
    salvarNoOutlookMutation.mutate(value);
  };
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({ open: false, message: "", severity: "info" });

  // Guard: previne que click fora do popup abra novo evento
  const popupJustClosedRef = useRef(false);

  const showSnackbar = useCallback(
    (message: string, severity: "success" | "error" | "info" = "success") => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  const dayColumns = useMemo(
    () => getDayColumns(events),
    [getDayColumns, events]
  );

  const allDayEvents = useMemo(
    () => events.filter((e) => e.isAllDay),
    [events]
  );

  const handleEventClick = useCallback(
    (event: CalendarEvent, element: HTMLElement) => {
      setSelectedEvent(event);
      setAnchorEl(element);
    },
    []
  );

  const handleClosePopup = useCallback(() => {
    setSelectedEvent(null);
    setAnchorEl(null);
    // Marca que o popup acabou de fechar para evitar abrir novo evento
    popupJustClosedRef.current = true;
    setTimeout(() => {
      popupJustClosedRef.current = false;
    }, 200);
  }, []);

  const handleNewEvent = useCallback(() => {
    setEditingEvent(null);
    setDefaultDate(undefined);
    setDefaultHour(undefined);
    setModalOpen(true);
  }, []);

  const handleTimeSlotClick = useCallback(
    (date: Date, hour: number, _minutes: number) => {
      // Ignora se o popup acabou de fechar
      if (popupJustClosedRef.current) return;
      setEditingEvent(null);
      setDefaultDate(date);
      setDefaultHour(hour);
      setModalOpen(true);
    },
    []
  );

  const handleEditEvent = useCallback((event: CalendarEvent) => {
    setEditingEvent(event);
    setSelectedEvent(null);
    setAnchorEl(null);
    setModalOpen(true);
  }, []);

  const handleSaveEvent = useCallback(
    async (payload: NewEventPayload) => {
      try {
        if (editingEvent) {
          await updateEvent.mutateAsync({
            eventId: editingEvent.id,
            event: payload,
          });
          showSnackbar("Evento atualizado com sucesso");
        } else {
          await createEvent.mutateAsync(payload);
          showSnackbar("Evento criado com sucesso");
        }
        setModalOpen(false);
        setEditingEvent(null);
      } catch (err: any) {
        showSnackbar(err?.message || "Erro ao salvar evento", "error");
      }
    },
    [editingEvent, createEvent, updateEvent, showSnackbar]
  );

  const handleDeleteEvent = useCallback(
    async (eventId: string) => {
      try {
        await deleteEvent.mutateAsync(eventId);
        handleClosePopup();
        showSnackbar("Evento excluído com sucesso");
      } catch (err: any) {
        showSnackbar(err?.message || "Erro ao excluir evento", "error");
      }
    },
    [deleteEvent, handleClosePopup, showSnackbar]
  );

  const handleAcceptEvent = useCallback(
    async (eventId: string) => {
      try {
        await acceptEvent.mutateAsync({ eventId });
        handleClosePopup();
        showSnackbar("Evento aceito");
      } catch (err: any) {
        showSnackbar(err?.message || "Erro ao aceitar evento", "error");
      }
    },
    [acceptEvent, handleClosePopup, showSnackbar]
  );

  const handleDeclineEvent = useCallback(
    async (eventId: string) => {
      try {
        await declineEvent.mutateAsync({ eventId });
        handleClosePopup();
        showSnackbar("Evento recusado");
      } catch (err: any) {
        showSnackbar(err?.message || "Erro ao recusar evento", "error");
      }
    },
    [declineEvent, handleClosePopup, showSnackbar]
  );

  const handleTentativeEvent = useCallback(
    async (eventId: string) => {
      try {
        await tentativeEvent.mutateAsync({ eventId });
        handleClosePopup();
        showSnackbar("Resposta enviada: Talvez");
      } catch (err: any) {
        showSnackbar(err?.message || "Erro ao responder evento", "error");
      }
    },
    [tentativeEvent, handleClosePopup, showSnackbar]
  );

  const handleMiniCalendarSelect = useCallback(
    (date: Date) => {
      goToDate(date);
      setMiniCalendarOpen(false);
    },
    [goToDate]
  );

  return (
    <>
      <NavCommon
        breadcrumbs={[{ path: "/calendario", name: "Calendário" }]}
        showButtonNew={false}
        onBack={() => history.goBack()}
        open={false}
        onChangeActionNew={undefined}
      />

      <S.PageContainer>
        <S.CalendarWrapper>
          <CalendarHeader
            currentDate={currentDate}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            onNavigate={navigate}
            onNewEvent={handleNewEvent}
            onNewTask={() => setNewTaskOpen(true)}
            onTitleClick={() => setMiniCalendarOpen((v) => !v)}
            onRefresh={() => refetch()}
            isRefreshing={isLoading}
            syncEnabled={syncEnabled}
            onSyncChange={handleSyncChange}
          />

          {/* Mini calendário dropdown */}
          {miniCalendarOpen && (
            <MiniCalendar
              currentDate={currentDate}
              onSelectDate={handleMiniCalendarSelect}
              onClose={() => setMiniCalendarOpen(false)}
            />
          )}

          {isLoading ? (
            <S.LoadingContainer>
              <S.Spinner />
              <span>Carregando eventos...</span>
            </S.LoadingContainer>
          ) : isError ? (
            <S.ErrorContainer>
              <span>
                {(error as Error)?.message ||
                  "Erro ao carregar eventos do calendário"}
              </span>
              <S.RetryButton onClick={() => refetch()}>
                Tentar novamente
              </S.RetryButton>
            </S.ErrorContainer>
          ) : (
            <S.CalendarGrid>
              <S.DayHeaderRow $columns={dayColumns.length}>
                <S.TimeGutterHeader />
                {dayColumns.map((col) => (
                  <S.DayHeaderCell
                    key={col.date.toISOString()}
                    $isToday={col.isToday}
                  >
                    <S.DayName $isToday={col.isToday}>{col.dayName}</S.DayName>
                    <S.DayNumber $isToday={col.isToday}>
                      {col.dayNumber}
                    </S.DayNumber>
                  </S.DayHeaderCell>
                ))}
              </S.DayHeaderRow>

              {allDayEvents.length > 0 && (
                <S.AllDayRow $columns={dayColumns.length}>
                  <S.AllDayGutter>dia todo</S.AllDayGutter>
                  {dayColumns.map((col) => {
                    const dayAllDayEvents = col.events.filter(
                      (e) => e.isAllDay
                    );
                    return (
                      <S.AllDayCell key={col.date.toISOString()}>
                        {dayAllDayEvents.map((evt) => (
                          <S.AllDayEvent
                            key={evt.id}
                            $showAs={evt.showAs}
                            $isCancelled={evt.isCancelled}
                            onClick={(e) =>
                              handleEventClick(evt, e.currentTarget)
                            }
                          >
                            {evt.isCancelled ? "Cancelado: " : ""}
                            {evt.subject || "Sem título"}
                          </S.AllDayEvent>
                        ))}
                      </S.AllDayCell>
                    );
                  })}
                </S.AllDayRow>
              )}

              <TimeGrid
                dayColumns={dayColumns}
                onEventClick={handleEventClick}
                onTimeSlotClick={handleTimeSlotClick}
              />
            </S.CalendarGrid>
          )}
        </S.CalendarWrapper>
      </S.PageContainer>

      <EventDetailPopup
        event={selectedEvent}
        anchorEl={anchorEl}
        onClose={handleClosePopup}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onAccept={handleAcceptEvent}
        onDecline={handleDeclineEvent}
        onTentative={handleTentativeEvent}
        currentUserEmail={userProfile?.mail || userProfile?.userPrincipalName}
      />

      <NewEventModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingEvent(null);
        }}
        onSave={handleSaveEvent}
        editingEvent={editingEvent}
        defaultDate={defaultDate}
        defaultHour={defaultHour}
        loading={createEvent.isLoading || updateEvent.isLoading}
      />

      <DialogCommon
        titleHeader="Nova tarefa"
        closeText="Fechar"
        saveText="Salvar"
        isFilter={false}
        isClose
        isRight={false}
        width={500}
        maxHeight="680px"
        open={newTaskOpen}
        setOpenModal={setNewTaskOpen}
        onClose={() => setNewTaskOpen(false)}
        onSave={() => {}}
        component={
          <NewTaskV2
            idLead=""
            idLeadCorretor=""
            lead={null}
            dataLead={null}
            closeModal={() => setNewTaskOpen(false)}
            refetch={() => {}}
            fromCalendario
            inserirNoOutlookOverride={syncEnabled}
          />
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Calendario;
